import React, { useState, useEffect } from 'react';
import { adminAPI, locationAPI } from '../../services/api';
import { TableSkeleton } from '../../components/skeletons';

const GREEN = '#1B5C3A', GOLD = '#F5A623', PURPLE = '#4C1D95', CHAR = '#1a1a1a', MUTED = '#6b7280', BORDER = '#e5e7eb';

const getCleanEmail = (email) => {
  if (!email) return '—';
  if (email.startsWith('deleted_')) {
    const parts = email.split('_');
    if (parts.length >= 3) {
      return parts.slice(2).join('_');
    }
  }
  return email;
};

const getCleanMobile = (mobile) => {
  if (!mobile) return '';
  if (mobile.startsWith('deleted_')) {
    const parts = mobile.split('_');
    if (parts.length >= 3) {
      return parts.slice(2).join('_');
    }
  }
  return mobile;
};

const badge = (status, accountStatus) => {
  if (accountStatus === 'deleted') {
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>Deleted</span>;
  }
  const m = { active: { bg: '#dcfce7', c: GREEN, l: 'Active' }, suspended: { bg: '#fee2e2', c: '#dc2626', l: 'Suspended' }, paused: { bg: '#fef3c7', c: '#d97706', l: 'Paused' } };
  const s = m[status] || m.active;
  return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.c }}>{s.l}</span>;
};

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', password: '', mobileNumber: '', city: '', state: '', firmName: '', gstin: '', isGstRegistered: false });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadVendors = () => { adminAPI.vendors().then(r => setVendors(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };

  useEffect(() => { loadVendors(); locationAPI.states().then(r => setStates(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (form.state) locationAPI.cities(form.state).then(r => setCities(r.data.data || [])).catch(() => setCities([])); else setCities([]); }, [form.state]);

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'active' ? 'suspended' : 'active';
    await adminAPI.updateVendorStatus(v._id, { status: newStatus });
    loadVendors();
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Are you sure you want to soft delete vendor "${v.userId?.fullName || v.firmName}"? This will deactivate and soft delete their account.`)) return;
    await adminAPI.deleteVendor(v._id);
    loadVendors();
  };

  const handleAdd = async () => {
    if (!form.fullName || !form.email || !form.password || !form.firmName || !form.city || !form.state) return alert('All fields required.');
    setSaving(true);
    try {
      await adminAPI.createVendor(form);
      setShowAdd(false);
      setForm({ fullName: '', email: '', password: '', mobileNumber: '', city: '', state: '', firmName: '', gstin: '', isGstRegistered: false });
      loadVendors();
    } catch (err) { alert(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const filtered = vendors.filter(v => {
    const userStatus = v.userId?.accountStatus || 'active';
    if (statusFilter !== 'all') {
      if (statusFilter === 'active' && userStatus !== 'active') return false;
      if (statusFilter === 'deleted' && userStatus !== 'deleted') return false;
    }

    const q = search.toLowerCase();
    if (!q) return true;

    const name = (v.userId?.fullName || '').toLowerCase();
    const email = (v.userId?.email || '').toLowerCase();
    const cleanEmail = getCleanEmail(email).toLowerCase();
    const mobile = (v.userId?.mobileNumber || '').toLowerCase();
    const cleanMobile = getCleanMobile(mobile).toLowerCase();
    const firmName = (v.firmName || '').toLowerCase();
    const city = (v.city || '').toLowerCase();

    return name.includes(q) || 
      email.includes(q) || 
      cleanEmail.includes(q) || 
      mobile.includes(q) || 
      cleanMobile.includes(q) || 
      firmName.includes(q) || 
      city.includes(q);
  });

  if (loading) return <TableSkeleton rows={8} columns={6} style={{ padding: 24 }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR }}>Vendor Management</h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>{vendors.length} registered vendors</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select 
            className="form-input" 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            style={{ width: 140, fontSize: 13, cursor: 'pointer', height: '38px', padding: '0 10px', boxSizing: 'border-box' }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="deleted">Deleted</option>
          </select>
          <input className="form-input" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220, fontSize: 13, height: '38px', boxSizing: 'border-box' }} />
          <button onClick={() => setShowAdd(true)} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', height: '38px' }}>+ Add Vendor</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Vendor', 'Firm', 'City', 'Wallet', 'Leads', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', borderBottom: `2px solid ${BORDER}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: MUTED }}>No vendors found</td></tr>
            ) : filtered.map(v => {
              const isDeleted = !v.userId || v.userId.accountStatus === 'deleted';
              return (
                <tr key={v._id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: CHAR }}>{v.userId?.fullName || '—'}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{v.userId ? getCleanEmail(v.userId.email) : '—'}</div>
                    {v.userId?.mobileNumber && (
                      <div style={{ fontSize: 11, color: MUTED }}>{getCleanMobile(v.userId.mobileNumber)}</div>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 13 }}>{v.firmName || '—'}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13 }}>{v.city}, {v.state}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 700, color: (v.walletBalance || 0) < 500 ? '#dc2626' : GREEN, fontSize: 13 }}>₹{(v.walletBalance || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: PURPLE }}>{v.leadCount || 0}</td>
                  <td style={{ padding: '12px 14px' }}>{badge(v.status, isDeleted ? 'deleted' : v.userId?.accountStatus)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    {isDeleted ? (
                      <span style={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>Deleted (Disabled)</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => toggleStatus(v)} style={{ background: v.status === 'active' ? '#fee2e2' : '#dcfce7', color: v.status === 'active' ? '#dc2626' : GREEN, border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          {v.status === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                        <button onClick={() => handleDelete(v)} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer', color: '#dc2626' }}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Vendor Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowAdd(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '28px 32px', width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Add New Vendor</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Password *</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Mobile</label><input className="form-input" value={form.mobileNumber} onChange={e => setForm({ ...form, mobileNumber: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Firm Name *</label><input className="form-input" value={form.firmName} onChange={e => setForm({ ...form, firmName: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">GSTIN</label><input className="form-input" value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <select className="form-input" value={form.state} onChange={e => setForm({ ...form, state: e.target.value, city: '' })} style={{ cursor: 'pointer' }}>
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">City *</label>
                <select className="form-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} disabled={!form.state} style={{ cursor: 'pointer' }}>
                  <option value="">{form.state ? 'Select City' : 'Select state first'}</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 16, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isGstRegistered} onChange={e => setForm({ ...form, isGstRegistered: e.target.checked })} /> GST Registered
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowAdd(false)} style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 9, padding: '8px 20px', fontSize: 13, cursor: 'pointer', color: MUTED }}>Cancel</button>
              <button onClick={handleAdd} disabled={saving} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '8px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Creating...' : 'Create Vendor'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
